// Ultimate Card Game (UCG) - Gamerank: post-match skill grade (C..S+)
//
// Pure function of a single team's MatchStats.getSummary() plus a win/loss
// flag - no UI state, no RNG, no clock. Both lockstep clients therefore
// compute the identical grade for the identical match without any network
// traffic. See docs/GAMERANK_PLAN.md for the full design rationale.

import { GAMERANK } from '../data/balance.js';

export function computeGamerank(summary, won, options = {}) {
  const rawTerritory = summary.avgTerritoryControl ?? 50;
  const territoryIndex = normalizeTerritory(rawTerritory);
  const damageIndex = normalizeDamage(summary.damagePerMinute ?? 0);
  const efficiencyRatio = summary.resourceEfficiency !== undefined
    ? summary.resourceEfficiency
    : (summary.plasmaSpent > 0 ? (summary.materialValueDestroyed / summary.plasmaSpent) : 0);
  const efficiencyIndex = normalizeEfficiency(efficiencyRatio);

  const baseHp = typeof summary.minOwnBaseHp === 'number' ? summary.minOwnBaseHp : (options.baseHp ?? 1500);
  const baseHpIndex = normalizeBaseHp(baseHp);

  const kills = summary.kills ?? 0;
  const deaths = summary.deaths ?? (options.enemySummary?.kills ?? 0);
  const kdIndex = normalizeKd(kills, deaths);

  let composite =
    territoryIndex * GAMERANK.weights.territory +
    damageIndex * GAMERANK.weights.damage +
    efficiencyIndex * GAMERANK.weights.efficiency +
    baseHpIndex * (GAMERANK.weights.baseHp ?? 0) +
    kdIndex * (GAMERANK.weights.kdRatio ?? 0);

  composite += won ? GAMERANK.winBonus : -GAMERANK.winBonus;

  // Tactical discipline: +3 bonus points if superweapon was not deployed
  const superweaponUsed = (summary.superweaponsUsed || 0) > 0;
  const noSuperweaponBonus = !superweaponUsed ? (GAMERANK.noSuperweaponBonus ?? 3) : 0;
  composite += noSuperweaponBonus;

  // Speedrun tempo bonus for decisive victories
  const duration = summary.duration !== undefined ? summary.duration : options.duration;
  let tempoBonus = 0;
  if (won && duration !== undefined && GAMERANK.tempoBonus) {
    if (duration <= GAMERANK.tempoBonus.fastThreshold) {
      tempoBonus = GAMERANK.tempoBonus.fastPoints ?? 2;
    } else if (duration <= GAMERANK.tempoBonus.mediumThreshold) {
      tempoBonus = GAMERANK.tempoBonus.mediumPoints ?? 1;
    }
  }
  composite += tempoBonus;

  // Economic plasma discipline: +1 point if plasma was never capped at 150 (no wasted income)
  const plasmaCapped = summary.plasmaEverCapped ?? options.tracker?.plasmaEverCapped ?? false;
  const plasmaDisciplineBonus = (won && !plasmaCapped) ? (GAMERANK.plasmaDisciplineBonus ?? 1) : 0;
  composite += plasmaDisciplineBonus;

  composite = Math.max(0, Math.min(100, composite));

  // Anti-abuse check:
  // Matches abandoned/lost under minDuration (e.g. instant restart spam) or with 0 combat activity
  let antiAbuseTriggered = false;
  if (duration !== undefined) {
    const minDuration = GAMERANK.antiAbuse?.minDurationSeconds ?? 45;
    const isEarlyQuit = !won && duration < minDuration;
    const hasCombatTracking = summary.cardsPlayed !== undefined || summary.damageDealt !== undefined;
    const isZeroEngagement = hasCombatTracking && (summary.cardsPlayed || 0) === 0 && (summary.damageDealt || 0) === 0 && duration >= 15;
    antiAbuseTriggered = isEarlyQuit || isZeroEngagement;
  }

  const dFloor = (GAMERANK.thresholds.c ?? 20) - 1;
  const finalComposite = antiAbuseTriggered ? Math.min(dFloor, Math.round(composite)) : Math.round(composite);
  const grade = antiAbuseTriggered ? 'D' : gradeFromComposite(finalComposite);

  return {
    grade,
    composite: finalComposite,
    breakdown: {
      territoryIndex: Math.round(territoryIndex),
      damageIndex: Math.round(damageIndex),
      efficiencyIndex: Math.round(efficiencyIndex),
      baseHpIndex: Math.round(baseHpIndex),
      kdIndex: Math.round(kdIndex),
      noSuperweaponBonus,
      tempoBonus,
      plasmaDisciplineBonus,
      antiAbuseTriggered
    }
  };
}

export function normalizeTerritory(avgTerritory) {
  if (!GAMERANK.territoryCalibration) {
    return Math.max(0, Math.min(100, avgTerritory));
  }
  const { low, high } = GAMERANK.territoryCalibration;
  if (high <= low) return 50;
  return Math.max(0, Math.min(100, ((avgTerritory - low) / (high - low)) * 100));
}

export function normalizeDamage(dpm) {
  const { low, high } = GAMERANK.damageCalibration;
  if (high <= low) return 0;
  return Math.max(0, Math.min(100, ((dpm - low) / (high - low)) * 100));
}

export function normalizeEfficiency(ratio) {
  const { low, high } = GAMERANK.efficiencyCalibration;
  if (high <= low) return 50;
  return Math.max(0, Math.min(100, ((ratio - low) / (high - low)) * 100));
}

export function normalizeBaseHp(minBaseHp) {
  const maxHp = GAMERANK.baseHpCalibration?.maxHp ?? 1500;
  if (maxHp <= 0) return 100;
  return Math.max(0, Math.min(100, (minBaseHp / maxHp) * 100));
}

export function normalizeKd(kills, deaths) {
  const { low, high } = GAMERANK.kdCalibration ?? { low: 1.0, high: 3.0 };
  const safeDeaths = Math.max(1, deaths);
  const ratio = kills / safeDeaths;
  if (high <= low) return 50;
  return Math.max(0, Math.min(100, ((ratio - low) / (high - low)) * 100));
}

export function gradeFromComposite(score) {
  const t = GAMERANK.thresholds;
  if (score >= t.sPlus) return 'S+';
  if (score >= t.s) return 'S';
  if (t.sMinus !== undefined && score >= t.sMinus) return 'S-';
  if (t.aPlus !== undefined && score >= t.aPlus) return 'A+';
  if (score >= t.a) return 'A';
  if (t.bPlus !== undefined && score >= t.bPlus) return 'B+';
  if (score >= t.b) return 'B';
  if (score >= (t.c ?? 20)) return 'C';
  return 'D';
}

// Reusable zero-allocation result container for 60 FPS / live meter HUD updates
const _liveProjectionResult = {
  grade: '--',
  composite: 0,
  isWarmup: true
};

/**
 * Computes live projected Gamerank during an ongoing match with ZERO heap allocations.
 * Assumes a victorious finish (since S-Rank only unlocks rewards upon victory).
 *
 * @param {object} teamStats - Direct reference to stats.teams[myTeam]
 * @param {number} totalTerritoryIntegral - westernIntegral + easternIntegral
 * @param {number} duration - Match duration in seconds
 * @returns {object} Reusable _liveProjectionResult with grade, composite, isWarmup
 */
export function computeLiveGamerankProjection(teamStats, totalTerritoryIntegral, duration) {
  const warmupSeconds = GAMERANK.warmupSeconds ?? 20;
  if (!teamStats || duration < warmupSeconds) {
    _liveProjectionResult.grade = '--';
    _liveProjectionResult.composite = 0;
    _liveProjectionResult.isWarmup = true;
    return _liveProjectionResult;
  }

  // 1. Damage per minute (floored at 1 minute to prevent early-game spike distortions)
  const effectiveMinutes = Math.max(1.0, duration / 60);
  const dpm = duration > 0 ? (teamStats.damageDealt / effectiveMinutes) : 0;
  const damageIndex = normalizeDamage(dpm);

  // 2. Resource efficiency
  const effRatio = teamStats.plasmaSpent > 0
    ? (teamStats.materialValueDestroyed / teamStats.plasmaSpent)
    : 0.75; // Neutral baseline if no plasma spent yet
  const efficiencyIndex = normalizeEfficiency(effRatio);

  // 3. Average territory control (calibrated)
  const rawTerritory = totalTerritoryIntegral > 0
    ? Math.max(0, Math.min(100, Math.round((teamStats.territoryControlTimeIntegral / totalTerritoryIntegral) * 100)))
    : 50;
  const territoryIndex = normalizeTerritory(rawTerritory);

  // 4. Base HP preservation
  const minBaseHp = typeof teamStats.minOwnBaseHp === 'number' ? teamStats.minOwnBaseHp : 1500;
  const baseHpIndex = normalizeBaseHp(minBaseHp);

  // 5. Kill-to-death ratio
  const kills = teamStats.kills || 0;
  const deaths = teamStats.deaths || 0;
  const kdIndex = normalizeKd(kills, deaths);

  let composite =
    territoryIndex * GAMERANK.weights.territory +
    damageIndex * GAMERANK.weights.damage +
    efficiencyIndex * GAMERANK.weights.efficiency +
    baseHpIndex * (GAMERANK.weights.baseHp ?? 0) +
    kdIndex * (GAMERANK.weights.kdRatio ?? 0) +
    GAMERANK.winBonus; // Projected on victory

  const superweaponUsed = (teamStats?.superweaponsUsed || 0) > 0;
  if (!superweaponUsed) {
    composite += (GAMERANK.noSuperweaponBonus ?? 3);
  }

  // Live speedrun bonus projection
  if (GAMERANK.tempoBonus) {
    if (duration <= GAMERANK.tempoBonus.fastThreshold) {
      composite += (GAMERANK.tempoBonus.fastPoints ?? 2);
    } else if (duration <= GAMERANK.tempoBonus.mediumThreshold) {
      composite += (GAMERANK.tempoBonus.mediumPoints ?? 1);
    }
  }

  // Live plasma discipline projection
  if (!teamStats?.plasmaEverCapped) {
    composite += (GAMERANK.plasmaDisciplineBonus ?? 1);
  }

  composite = Math.max(0, Math.min(100, composite));
  const roundedComposite = Math.round(composite);

  _liveProjectionResult.grade = gradeFromComposite(roundedComposite);
  _liveProjectionResult.composite = roundedComposite;
  _liveProjectionResult.isWarmup = false;

  return _liveProjectionResult;
}
